import React, { useState, useEffect } from "react";
import "./QuestionPaper.css";
import WarningDialog from "./WarningDialog";
import QRCode from "react-qr-code";
import ImagePreviewDialog from "./ImagePreviewDialog";

/**
 * Question Paper Component
 *
 * API Response Structure:
 * {
 *   examId: string,
 *   studentId: string,
 *   sections: [
 *     {
 *       sectionName: string,
 *       sectionType: "MCQ" | "Descriptive",
 *       maxAttemptQuestions: number,
 *       totalQuestions: number,
 *       maxMarksPerQuestion: number,
 *       questions: [
 *         {
 *           questionId: string,
 *           option: string, // For MCQ questions
 *           answer: string, // The answer text
 *           fileUrl: string // URL for offline answers
 *         }
 *       ]
 *     }
 *   ]
 * }
 */

const MCQQuestion = ({ question, options, onAnswerChange, currentAnswer }) => {
  return (
    <div className="mcq-question">
      <p className="question-text">{question}</p>
      <div className="options-container">
        {options.map((option, index) => (
          <label key={index} className="option-label">
            <input
              type="radio"
              name="mcq-option"
              value={option}
              checked={currentAnswer?.response === option}
              onChange={(e) =>
                onAnswerChange({
                  response: e.target.value,
                  responseType: "online",
                })
              }
            />
            <span className="option-text">{option}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

const SubjectiveQuestion = ({ question, onAnswerChange, currentAnswer }) => {
  const handleResponseTypeChange = (type) => {
    onAnswerChange({
      response: type === "online" ? currentAnswer?.response || "" : "",
      responseType: type,
    });
  };

  return (
    <div className="subjective-question">
      <p className="question-text">{question}</p>

      <div className="answer-mode-selector">
        <label className="mode-label">
          <input
            type="radio"
            name="answer-mode"
            value="online"
            checked={currentAnswer?.responseType !== "offline"}
            onChange={() => handleResponseTypeChange("online")}
          />
          <span>Answer Online</span>
        </label>
        <label className="mode-label">
          <input
            type="radio"
            name="answer-mode"
            value="offline"
            checked={currentAnswer?.responseType === "offline"}
            onChange={() => handleResponseTypeChange("offline")}
          />
          <span>Write on Paper</span>
        </label>
      </div>

      {currentAnswer?.responseType === "offline" ? (
        <div className="offline-message">
          You have chosen to write this answer on paper. Please ensure to
          include this answer in your final PDF upload.
        </div>
      ) : (
        <textarea
          className="answer-textarea"
          placeholder="Type your answer here..."
          value={currentAnswer?.response || ""}
          onChange={(e) =>
            onAnswerChange({ response: e.target.value, responseType: "online" })
          }
        />
      )}
    </div>
  );
};

const QuestionPaper = ({ examId }) => {
  const [questions, setQuestions] = useState([]);
  const [sections, setSections] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasOfflineAnswers, setHasOfflineAnswers] = useState(false);
  const [isFirstMount, setIsFirstMount] = useState(true);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(false);

  // Clear localStorage on first mount
  useEffect(() => {
    if (isFirstMount) {
      console.log("[EXAM] First mount - clearing previous exam data");
      localStorage.removeItem("examAnswers");
      setIsFirstMount(false);
    }
  }, [isFirstMount]);

  // Load answers from localStorage on component mount
  useEffect(() => {
    if (!isFirstMount) {
      // Only load answers if not first mount
      const savedData = localStorage.getItem("examAnswers");
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        const answersObj = {};

        // Convert sections of questions back to answers object format
        if (parsedData.sections) {
          // Handle new format with sections
          parsedData.sections.forEach((section) => {
            section.questions.forEach((q) => {
              // Use stored responseType if available, otherwise infer from isOffline flag
              const responseType =
                q.responseType || (q.isOffline ? "offline" : "online");

              answersObj[q.questionId] = {
                response: q.answer || q.option || "",
                responseType: responseType,
              };
            });
          });
        } else if (parsedData.responses) {
          // Handle old format for backward compatibility
          parsedData.responses.forEach((item) => {
            answersObj[item.questionId] = {
              response: item.response,
              responseType: item.responseType,
            };
          });
        }

        setAnswers(answersObj);

        // Check for offline answers
        setHasOfflineAnswers(
          Object.values(answersObj).some(
            (item) => item.responseType === "offline"
          )
        );
      }
    }
  }, [isFirstMount]);

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const response = await fetch(
          `https://procbackend.elcarreira.com/exam/getQuestionPaper?examId=${examId}`
        );
        const data = await response.json();

        if (data.status === 200) {
          setSections(data.data.sections);
          // Extract and format questions from sections
          const formattedQuestions = data.data.sections.flatMap((section) =>
            section.questions.map((q) => ({
              ...q,
              questionType:
                section.sectionType.toLowerCase() === "objective"
                  ? "mcq"
                  : "subjective",
              sectionName: section.sectionName,
              maxAttemptQuestions: section.maxAttemptQuestions,
              totalQuestions: section.totalQuestions,
              maxMarksPerQuestion: section.maxMarksPerQuestion,
            }))
          );
          setQuestions(formattedQuestions);
        } else {
          throw new Error("Failed to fetch questions");
        }
      } catch (err) {
        setError("Failed to load questions. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [examId]);

  const handleAnswerChange = (answer) => {
    setAnswers((prev) => ({
      ...prev,
      [questions[currentQuestionIndex]._id]: answer,
    }));
  };

  // Save answers to localStorage in the required format
  const saveAnswersToLocalStorage = () => {
    // Create sectioned structure for answers
    const sectionsMap = {};

    Object.entries(answers).forEach(([questionId, answer]) => {
      // Find the question from questions array
      const question = questions.find((q) => q._id === questionId);
      if (!question) return;

      const sectionName = question.sectionName;

      // Initialize section if not exists
      if (!sectionsMap[sectionName]) {
        const sectionInfo = sections.find((s) => s.sectionName === sectionName);
        sectionsMap[sectionName] = {
          sectionName,
          sectionType:
            sectionInfo?.sectionType ||
            (question.questionType === "mcq" ? "MCQ" : "Descriptive"),
          maxAttemptQuestions: question.maxAttemptQuestions,
          totalQuestions: question.totalQuestions,
          maxMarksPerQuestion: question.maxMarksPerQuestion,
          questions: [],
        };
      }

      // Add question to section
      const isOffline = answer.responseType === "offline";
      const fileUrl = isOffline ? getUploadURL(questionId) : "";

      sectionsMap[sectionName].questions.push({
        questionId,
        option: question.questionType === "mcq" ? answer.response : "",
        answer: answer.response || "",
        fileUrl: fileUrl,
        isOffline: isOffline, // Additional metadata for client-side tracking
        responseType: answer.responseType || "online", // Store responseType for client-side use
      });
    });

    // Convert map to array of sections
    const sectionsArray = Object.values(sectionsMap);

    const payload = {
      examId,
      studentId: window.appState?.clientId,
      sections: sectionsArray,
    };

    localStorage.setItem("examAnswers", JSON.stringify(payload));

    // Check for offline answers
    const hasOfflineAnswers = Object.values(answers).some(
      (answer) => answer.responseType === "offline"
    );
    setHasOfflineAnswers(hasOfflineAnswers);
  };

  const goToNextQuestion = () => {
    const currentAnswer = answers[questions[currentQuestionIndex]._id];
    if (!currentAnswer?.response && currentAnswer?.responseType !== "offline") {
      if (
        window.confirm(
          "You haven't answered this question. Are you sure you want to proceed?"
        )
      ) {
        saveAnswersToLocalStorage(); // Save answers before moving to next question
        setCurrentQuestionIndex((prev) =>
          Math.min(prev + 1, questions.length - 1)
        );
      }
    } else {
      saveAnswersToLocalStorage(); // Save answers before moving to next question
      setCurrentQuestionIndex((prev) =>
        Math.min(prev + 1, questions.length - 1)
      );
    }
  };

  const goToPreviousQuestion = () => {
    setCurrentQuestionIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleSubmitClick = () => {
    setShowSubmitDialog(true);
  };

  const handleDialogClose = () => {
    setShowSubmitDialog(false);
  };

  const submitAnswers = async () => {
    if (isSubmitting) return; // Prevent double submission
    setIsSubmitting(true);
    console.log("[EXAM] Starting submission process...");

    try {
      // Save final answers before submitting
      saveAnswersToLocalStorage();
      console.log("[EXAM] Saved answers to localStorage");

      // Get the saved data
      const savedData = JSON.parse(localStorage.getItem("examAnswers"));
      console.log("[EXAM] Submitting answers:", savedData);

      // Submit responses to API
      console.log("[EXAM] Submitting responses to API...");
      const response = await fetch(
        "https://procbackend.elcarreira.com/exam/saveResponses",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(savedData),
        }
      );

      const responseData = await response.json();
      console.log("[EXAM] API response:", responseData);

      if (!response.ok || responseData.status !== 200) {
        throw new Error(responseData.message || "Failed to submit answers");
      }
      console.log("[EXAM] API submission successful");

      // Call setEndExam after successful submission
      const setEndExamResponse = await fetch(
        `https://procbackend.elcarreira.com/exam/setEndExam?studentId=${window.appState.clientId}&examId=${window.appState.examId}`,
        {
          method: "POST",
        }
      );

      const endExamData = await setEndExamResponse.json();
      console.log("[EXAM] setEndExam response:", endExamData);

      if (!setEndExamResponse.ok || !endExamData.message?.acknowledged) {
        throw new Error("Failed to mark exam as completed");
      }
      console.log("[EXAM] Successfully marked exam as completed");

      // Close the dialog
      setShowSubmitDialog(false);

      // Clear localStorage
      localStorage.removeItem("examAnswers");
      console.log("[EXAM] Cleared localStorage");

      // Update appState
      window.appState = {
        ...window.appState,
        mode: "EXAM_COMPLETE",
        examEndReason: "SUBMITTED",
        isExamStarted: false,
      };
      console.log("[EXAM] Updated window.appState:", window.appState);

      // Emit event for exam completion
      const event = new CustomEvent("examStateChange", {
        detail: {
          mode: "EXAM_COMPLETE",
          reason: "SUBMITTED",
          timestamp: new Date().toISOString(),
        },
      });
      console.log("[EXAM] Dispatching examStateChange event:", event);
      window.dispatchEvent(event);

      // Call handleSessionEnd to clean up
      if (window.handleSessionEnd) {
        console.log("[EXAM] Calling handleSessionEnd with reason: SUBMITTED");
        window.handleSessionEnd("SUBMITTED");
      } else {
        console.warn(
          "[EXAM] handleSessionEnd not found, falling back to stopPlayer"
        );
        if (window.stopPlayer) {
          console.log("[EXAM] Stopping player");
          try {
            window.stopPlayer();
          } catch (error) {
            console.error("[EXAM] Error stopping player:", error);
          }
        }
      }
    } catch (error) {
      console.error("[EXAM] Error submitting answers:", error);
      alert(error.message || "Failed to submit answers. Please try again.");
      setIsSubmitting(false);
      setShowSubmitDialog(false);
    }
  };

  // Function to generate upload URL with query parameters
  const getUploadURL = (questionId) => {
    const queryParams = new URLSearchParams({
      studentId: (window.appState?.clientId || "").trim(),
      examId: (examId || "").trim(),
      questionId: (questionId || "").trim(),
    });
    // Use production URL instead of localhost for actual file uploads
    return `https://talupload.elcarreira.com/upload?${queryParams.toString()}`;
  };

  // Function to handle showing/hiding QR code
  const toggleQRCode = () => {
    setShowQRCode(!showQRCode);
  };

  if (loading) {
    return (
      <div className="question-paper-loading">
        <p>Loading questions...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="question-paper-error">
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  if (!questions || questions.length === 0) {
    return (
      <div className="question-paper-error">
        <p>No questions available.</p>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const currentAnswer = answers[currentQuestion._id];

  // Add this inside the return statement where the question content is rendered
  const renderUploadButton = () => {
    if (currentAnswer?.responseType === "offline") {
      return (
        <div className="answer-upload-section">
          <div className="upload-buttons">
            <button className="upload-answer-btn" onClick={toggleQRCode}>
              Upload Answer
            </button>
            <button
              className="view-uploaded-btn"
              onClick={() => setShowImagePreview(true)}
            >
              View Uploaded
            </button>
          </div>
          {showQRCode && (
            <div className="qr-code-overlay">
              <div className="qr-code-dialog">
                <div className="qr-code-header">
                  <h3>Upload Answer</h3>
                  <button
                    className="qr-code-close-button"
                    onClick={toggleQRCode}
                  >
                    ×
                  </button>
                </div>
                <div className="qr-code-container">
                  <QRCode
                    value={getUploadURL(currentQuestion._id)}
                    size={200}
                    level="H"
                  />
                  <p className="qr-code-instruction">
                    Scan QR code with your phone to upload answer for this
                    question
                  </p>
                </div>
              </div>
            </div>
          )}
          <ImagePreviewDialog
            isOpen={showImagePreview}
            onClose={() => setShowImagePreview(false)}
            questionId={currentQuestion._id}
            studentId={window.appState?.clientId}
          />
        </div>
      );
    }
    return null;
  };

  return (
    <div className="question-paper">
      <div className="question-header">
        <h3>
          Question {currentQuestionIndex + 1} of {questions.length}
        </h3>
        <div className="question-type-badge">
          {currentQuestion.questionType === "mcq"
            ? "Multiple Choice"
            : "Subjective"}
        </div>
      </div>

      <div className="section-info">
        <h4>{currentQuestion.sectionName}</h4>
        <p className="section-instructions">
          Answer any {currentQuestion.maxAttemptQuestions} out of{" "}
          {currentQuestion.totalQuestions} questions. Each question carries{" "}
          {currentQuestion.maxMarksPerQuestion} marks.
        </p>
      </div>

      <div className="question-content">
        {currentQuestion.questionType === "mcq" ? (
          <MCQQuestion
            question={currentQuestion.question}
            options={currentQuestion.options}
            onAnswerChange={handleAnswerChange}
            currentAnswer={currentAnswer}
          />
        ) : (
          <SubjectiveQuestion
            question={currentQuestion.question}
            onAnswerChange={handleAnswerChange}
            currentAnswer={currentAnswer}
          />
        )}
        {renderUploadButton()}
      </div>

      <div className="navigation-buttons">
        <button
          onClick={goToPreviousQuestion}
          disabled={currentQuestionIndex === 0}
          className="nav-button prev"
        >
          Previous
        </button>

        {currentQuestionIndex === questions.length - 1 ? (
          <button
            onClick={handleSubmitClick}
            className="nav-button submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Submit Answers"}
          </button>
        ) : (
          <button
            onClick={goToNextQuestion}
            disabled={currentQuestionIndex === questions.length - 1}
            className="nav-button next"
          >
            Next
          </button>
        )}
      </div>

      <WarningDialog
        isOpen={showSubmitDialog}
        onClose={handleDialogClose}
        message={
          <>
            <p>Are you sure you want to submit your exam?</p>
            <p>This action cannot be undone.</p>
            {hasOfflineAnswers && (
              <p>
                You have offline answers. After submission, you will be taken to
                the upload screen.
              </p>
            )}
            <div className="submit-actions">
              <button
                onClick={submitAnswers}
                className="submit-confirm-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Yes, Submit Exam"}
              </button>
              <button
                onClick={handleDialogClose}
                className="submit-cancel-btn"
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </>
        }
        hideWarningCount={true}
      />
    </div>
  );
};

export default QuestionPaper;
